import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import ModelSelector from "./components/ModelSelector";
import ConfigurationType from "./components/ConfigurationType";
import DimensionInput from "./components/DimensionInput";
import PostOptions from "./components/PostOptions";
import GateConfiguration from "./components/GateConfiguration";
import ColorSelector from "./components/ColorSelector";
import AccessoriesConfiguration from "./components/AccessoriesConfiguration";
import PriceDisplay from "./components/PriceDisplay";
import ContactForm from "./components/ContactForm";
import { fenceModels, calculateTotalPrice } from "./data/mockPricing";
import { calculateAccessoriesTotal } from "./data/accessoriesPricing";
import { Fence, Calculator } from "lucide-react";

const Home = () => {
  const [selectedModel, setSelectedModel] = useState(null);
  const [configurationType, setConfigurationType] = useState('fence-only');
  const [selectedColor, setSelectedColor] = useState('RAL7016');
  const [length, setLength] = useState('');
  const [height, setHeight] = useState('');
  const [includePosts, setIncludePosts] = useState(true);
  const [postSize, setPostSize] = useState('10x10');
  const [panels, setPanels] = useState([{ width: '', height: '', quantity: 1 }]);
  const [gates, setGates] = useState({
    pedestrian: { enabled: false, items: [] },
    double: { enabled: false, items: [] },
    sliding: { enabled: false, items: [] }
  });
  const [gatePostSize, setGatePostSize] = useState('none');
  const [gatePostQuantity, setGatePostQuantity] = useState('0');
  const [accessories, setAccessories] = useState({
    briefkasten: { enabled: false, variant: null, quantity: 1 },
    hausnummer: { enabled: false, variant: null, quantity: 1 },
    klingel: { enabled: false, variant: null, quantity: 1 }
  });
  const [priceData, setPriceData] = useState(null);

  useEffect(() => {
    if (selectedModel) {
      const lengthNum = parseInt(length) || 0;
      const heightNum = parseInt(height) || 0;
      
      const calculated = calculateTotalPrice(
        configurationType,
        selectedModel.id,
        lengthNum,
        heightNum,
        includePosts,
        postSize,
        gates,
        panels,
        gatePostSize,
        gatePostQuantity
      );
      
      // Add accessories to price calculation
      const accessoriesData = calculateAccessoriesTotal(accessories);
      calculated.accessoriesData = accessoriesData;
      calculated.subtotal = (parseInt(calculated.subtotal) + accessoriesData.total).toString();
      
      // Recalculate transport cost and grand total
      const newSubtotal = parseInt(calculated.subtotal);
      const transportCost = 0;
      calculated.transportCost = '0';
      calculated.grandTotal = newSubtotal.toString();
      calculated.freeDelivery = false;
      
      setPriceData(calculated);
    } else {
      setPriceData(null);
    }
  }, [selectedModel, configurationType, length, height, includePosts, postSize, gates, panels, gatePostSize, gatePostQuantity, accessories]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-600 p-3 rounded-lg shadow-lg">
                <Fence className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent">
                  MAK Zaunplaner
                </h1>
                <p className="text-sm text-gray-600">Aluminium Zaun & Tor Konfigurator</p>
              </div>
            </div>
            <a
              href="https://www.smart-mak.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors duration-200"
            >
              www.smart-mak.com
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-pulse">
            <Calculator className="w-4 h-4" />
            Echtzeit-Preisberechnung
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Konfigurieren Sie Ihren <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Traumzaun</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Wählen Sie aus unseren Premium-Aluminiumzäunen und Toranlagen
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 pb-16">
        <div className="space-y-8">
          {/* Model Selection */}
          <section className="animate-fadeIn">
            <ModelSelector
              models={fenceModels}
              selectedModel={selectedModel}
              onSelectModel={setSelectedModel}
            />
          </section>

          {/* Configuration Type */}
          {selectedModel && (
            <section className="animate-slideUp">
              <ConfigurationType
                selectedModel={selectedModel}
                configurationType={configurationType}
                onConfigurationTypeChange={setConfigurationType}
              />
            </section>
          )}

          {/* Configuration and Results */}
          {selectedModel && (
            <div className="space-y-8 animate-slideUp max-w-6xl mx-auto">
              {/* Configuration Section */}
              <div className="space-y-6">
                {/* Fence Configuration */}
                {(configurationType === 'fence-only' || configurationType === 'fence-and-gates') && (
                  <>
                    <PostOptions
                      includePosts={includePosts}
                      postSize={postSize}
                      onIncludePostsChange={setIncludePosts}
                      onPostSizeChange={setPostSize}
                      selectedModel={selectedModel}
                    />
                    
                    <DimensionInput
                      length={length}
                      height={height}
                      onLengthChange={setLength}
                      onHeightChange={setHeight}
                      selectedModel={selectedModel}
                      includePosts={includePosts}
                      panels={panels}
                      onPanelsChange={setPanels}
                    />
                  </>
                )}

                {/* Gates Configuration */}
                {(configurationType === 'gates-only' || configurationType === 'fence-and-gates') && (
                  <GateConfiguration
                    selectedModel={selectedModel}
                    gates={gates}
                    onGatesChange={setGates}
                    gatePostSize={gatePostSize}
                    onGatePostSizeChange={setGatePostSize}
                    gatePostQuantity={gatePostQuantity}
                    onGatePostQuantityChange={setGatePostQuantity}
                  />
                )}

                {/* Color Selection */}
                <ColorSelector
                  selectedColor={selectedColor}
                  onColorChange={setSelectedColor}
                />

                {/* Accessories Configuration */}
                <AccessoriesConfiguration
                  selectedAccessories={accessories}
                  onAccessoriesChange={setAccessories}
                />
              </div>

              {/* Price Display and Contact Form */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Price Display */}
                <PriceDisplay
                  priceData={priceData}
                  selectedModel={selectedModel}
                  configurationType={configurationType}
                  length={length}
                  height={height}
                  postSize={postSize}
                  includePosts={includePosts}
                  selectedColor={selectedColor}
                />

                {/* Contact Form */}
                <ContactForm
                  selectedModel={selectedModel}
                  configurationType={configurationType}
                  length={length}
                  height={height}
                  priceData={priceData}
                  includePosts={includePosts}
                  postSize={postSize}
                  panels={panels}
                  gates={gates}
                  gatePostSize={gatePostSize}
                  gatePostQuantity={gatePostQuantity}
                  accessories={accessories}
                  selectedColor={selectedColor}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2025 MAK - Aluminium Zaun und Toranlagen
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Alle Preise inkl. MwSt. Änderungen und Irrtümer vorbehalten.
          </p>
        </div>
      </footer>

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;